import React from 'react';
import TimeAgo from 'timeago-react';
import {withRouter} from 'react-router-dom';
import moment from 'moment';

import Base from 'Components/Base/Base';
import Loading from 'Components/Partials/Layouts/Loading';

import Sockets from 'Services/Sockets';

import NotificationsApi from 'Services/Api/Me/Notifications';

class Notifications extends Base {
    state = {
        working: false,
        notifications: null,
        lastPage: 1,
        canLoadMore: false
    };

    notificationsContainer = null;

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.notificationsContainer.addEventListener('scroll', this.loadMore);

        Sockets.notification((notification) => {
            this.setState({
                notifications: [
                    notification,
                    ...this.state.notifications
                ]
            });
        });

        this.fetchNotifications(1);
    };

    /**
     * @method handleMarkAsRead
     * @return {Promise<void>}
     */
    handleMarkAsRead = async () => {
        const request = await NotificationsApi.readAll();

        if (request.status === 200) {
            this.toastSuccess('All notifications have been updated');

            this.setState({
                notifications: this.state.notifications.map(n => ({
                    ...n,
                    read_at: moment()
                }))
            })
        }
    };

    /**
     * @method loadMore
     */
    loadMore = () => {
        if (
            !this.state.canLoadMore ||
            this.notificationsContainer.clientHeight + this.notificationsContainer.scrollTop <
            this.notificationsContainer.scrollHeight
        ) {
            return;
        }

        this.fetchNotifications(this.state.lastPage + 1);
    };

    /**
     * @method fetchNotifications
     * @param page
     * @returns {Promise<boolean>}
     */
    fetchNotifications = async (page) => {
        this.working();

        const request = await NotificationsApi.fetch(page);

        if (request.status !== 200) {
            this.toastError('Sorry, your notifications could not be loaded');
            return false;
        }

        this.setState({
            working: false,
            notifications: [
                ...this.state.notifications ?? [],
                ...request.data.data
            ],
            lastPage: request.data.meta.current_page,
            canLoadMore: request.data.meta.current_page < request.data.meta.last_page
        });
    };

    /**
     * @method goToNotification
     * @param {object} notification
     * @returns {Promise<boolean>}
     */
    goToNotification = async (notification) => {
        const request = await NotificationsApi.read(notification.id);

        if (request.status !== 200) {
            this.toastError('Sorry, there was a problem updating your notification');
            return false;
        }

        const {notifications} = this.state;

        request.data.data.forEach(n => {
            const i = notifications.map(not => not.id).indexOf(n.id);

            if (i !== -1) {
                notifications[i] = n;
            }
        });

        this.setState({
            notifications
        }, () => {
            const notification = request.data.data[0];

            if (notification.data.link_type !== null) {
                this.linkTo(notification);
                this.props.onClose();
            }
        });
    };

    /**
     * @method linkTo
     */
    linkTo = (notification) => {
        let route = null;

        switch (notification.data.link_type) {
            case 'customers':
                route = `/customers${notification.data.link_id ? `/${notification.data.link_id}` : ''}`;
                break;
            case 'accident-log':
                route = `/accident-log/${notification.data.link_id}`;
                break;
            case 'noticeboard':
                route = '/noticeboard';
                break;
            case 'holiday-approvals':
                route = '/holidays';
                break;
            case 'my-holidays':
                route = '/hr/my-holidays';
                break;
            case 'proposal-review':
            case 'proposal-view':
                route = `/proposals/${notification.data.link_id}`;
                break;
            case 'events':
                route = `/diary/${notification.data.link_id}`;
                break;
            case 'time':
                route = `/time/${notification.data.link_id}`;
                break;
            case 'attendances':
                route = `/hr/attendances/${notification.data.link_id}`;
                break;
            case 'vehicles':
                route = `/vehicles/${notification.data.link_id}`;
                break;
            case 'parts':
                route = `/inventory/parts/${notification.data.link_id}`;
                break;
            case 'project-parts':
                route = `/jobs/${notification.data.link_id}/parts`;
                break;
            case 'downloads':
                route = '/downloads';
                break;
            case 'scheduling':
                route = '/scheduling';
                break;
            case 'technical-bulletin':
                route = '/documents/technical-bulletin';
                break;
            case 'build-manual':
                route = '/documents/build-manual';
                break;
            case 'drawing':
                route = '/documents/drawing';
                break;
            case 'data-sheet':
                route = '/documents/data-sheet';
                break;
        }

        if (route !== null) {
            this.props.history.push(route);
        }
    };

    /**
     * @method render
     * @return {*}
     */
    render() {
        const {working} = this.state;

        return (
            <div
                className={
                    'origin-top-right absolute right-0 mt-2 sm:w-90 md:w-96 rounded-md ' +
                    'shadow-lg'
                }
            >
                <div className="bg-white text-right">
                    <button className="text-sm p-2" onClick={this.handleMarkAsRead}>Mark all as read</button>
                </div>

                <div
                    className="bg-white shadow-xs max-h-vh-2/3 sm:w-screen md:w-full overflow-y-scroll"
                    ref={ref => this.notificationsContainer = ref}
                >
                    {this.state.notifications !== null && this.renderNotifications()}

                    {working && (
                        <Loading/>
                    )}
                </div>
            </div>
        );
    };

    /**
     * @method renderNotifications
     * @return {*}
     */
    renderNotifications = () => {
        const notifications = [];

        this.state.notifications.forEach((n) => {
            notifications.push((
                <div
                    key={n.id}
                    className={
                        `block p-2 text-sm text-gray-700 ${n.read_at === null ? 'bg-gray-200' : ''} hover:bg-gray-200 transition
                        ease-in-out duration-150 border-b border-gray-200 cursor-pointer`
                    }
                    onClick={() => this.goToNotification(n)}
                >
                    <div className="flex">
                        <div className="shrink-0">
                            <div
                                className={
                                    'flex items-center justify-center h-8 w-8 ' +
                                    'rounded-full bg-indigo-500 text-white'
                                }
                            >
                                {n.user !== undefined && n.user !== null && (
                                    <img
                                        src={n.user.avatar_url}
                                        className="rounded-full"
                                        alt={`${n.user.first_name} ${n.user.last_name}`}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="ml-4 w-full">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-medium leading-5">{n.data.title}</h3>
                                <p className="text-xs leading-5">
                                    <TimeAgo datetime={n.created_at}/>
                                </p>
                            </div>

                            <p className="text-sm leading-5">
                                {n.data.message}
                            </p>
                        </div>
                    </div>
                </div>
            ));
        });

        if (notifications.length < 1) {
            notifications.push((
                <div
                    className={
                        'block p-4 text-sm text-gray-700 ' +
                        'hover:bg-gray-100 transition ease-in-out ' +
                        'duration-150'
                    }
                >
                    <div className="flex">
                        <div className="shrink-0 mx-auto">
                            <span>No notifications</span>
                        </div>
                    </div>
                </div>
            ));
        }

        return notifications;
    };
}

export default withRouter(Notifications);
